import type { GuestTestResult } from '@/data/guestTests';
import {
  getSharedQuestionById,
  aggregatePoolAnswersToFrameworks,
  getStoredPoolAnswers,
  type StoredPoolAnswer,
} from '@/data/sharedQuestionPool';

const GUEST_RESULTS_KEY = 'psyforge:anon:guest_quiz_results';

export interface PersonalitySignal {
  framework: string;
  signal_type: string;
  signal_data: Record<string, unknown>;
  confidence: number;
  source_type: string;
}

// ── Per-quiz mapping (existing quizzes with custom scales) ──

function mapLegacyQuizToSignals(result: GuestTestResult): PersonalitySignal[] {
  const signals: PersonalitySignal[] = [];
  const scaleMap: Record<string, number> = {};
  result.scales.forEach(s => { scaleMap[s.key] = s.score; });
  const source = `guest-quiz:${result.testSlug}`;

  switch (result.testSlug) {
    case 'dark-triad': {
      const avg = ((scaleMap.mach ?? 0) + (scaleMap.narc ?? 0) + (scaleMap.psych ?? 0)) / 3;
      signals.push({
        framework: 'alignment', signal_type: 'moral_axis_hint',
        signal_data: { axis: 'moral', score: 100 - avg, raw: scaleMap, interpretation: avg > 60 ? 'evil-leaning' : avg > 40 ? 'neutral' : 'good-leaning' },
        confidence: 0.35, source_type: source,
      });
      signals.push({
        framework: 'big_five', signal_type: 'dimension_hint',
        signal_data: { dimension: 'agreeableness', score: 100 - avg, direction: 'inverse_dark_triad' },
        confidence: 0.3, source_type: source,
      });
      break;
    }
    case 'emotional-intelligence': {
      const empathy = scaleMap.empathy ?? 50;
      const awareness = scaleMap.awareness ?? 50;
      const regulation = scaleMap.regulation ?? 50;
      const social = scaleMap.social ?? 50;
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: empathy, source_scale: 'empathy' }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'openness', score: awareness, source_scale: 'self_awareness' }, confidence: 0.25, source_type: source });
      const secureScore = (regulation + empathy) / 2;
      signals.push({ framework: 'attachment', signal_type: 'style_hint', signal_data: { secure_tendency: secureScore, social_skill: social }, confidence: 0.2, source_type: source });
      break;
    }
    case 'introvert-extrovert': {
      const introversion = scaleMap.introversion ?? 50;
      const extroversion = scaleMap.extroversion ?? 50;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'E_I', extroversion_score: extroversion, introversion_score: introversion }, confidence: 0.4, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: extroversion }, confidence: 0.4, source_type: source });
      break;
    }
    case 'stress-response': {
      const fight = scaleMap.fight ?? scaleMap.reactivity ?? 50;
      const freeze = scaleMap.freeze ?? 50;
      const fawn = scaleMap.fawn ?? scaleMap.coping ?? 50;
      const neuroScore = (fight + freeze) / 2;
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'neuroticism', score: neuroScore, raw: scaleMap }, confidence: 0.35, source_type: source });
      signals.push({ framework: 'attachment', signal_type: 'style_hint', signal_data: { anxious_tendency: fight, avoidant_tendency: freeze, fawn_tendency: fawn }, confidence: 0.2, source_type: source });
      break;
    }
    case 'love-style': {
      signals.push({ framework: 'attachment', signal_type: 'style_hint', signal_data: { love_style_scores: { ...scaleMap }, primary_label: result.primaryLabel }, confidence: 0.35, source_type: source });
      break;
    }
    case 'resilience': {
      const perseverance = scaleMap.perseverance ?? 50;
      const adaptability = scaleMap.adaptability ?? 50;
      const optimism = scaleMap.optimism ?? 50;
      const selfReliance = scaleMap.selfReliance ?? 50;
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'conscientiousness', score: perseverance }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'neuroticism', score: 100 - ((optimism + adaptability) / 2) }, confidence: 0.25, source_type: source });
      // High resilience correlates with secure attachment
      const secureScore = (optimism + adaptability + selfReliance) / 3;
      signals.push({ framework: 'attachment', signal_type: 'style_hint', signal_data: { secure_tendency: secureScore, resilience_based: true }, confidence: 0.2, source_type: source });
      break;
    }
    case 'conflict-style': {
      const competing = scaleMap.competing ?? 50;
      const collaborating = scaleMap.collaborating ?? 50;
      const accommodating = scaleMap.accommodating ?? 50;
      const avoiding = scaleMap.avoiding ?? 50;
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: (accommodating + collaborating) / 2, source_scale: 'conflict_style' }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: 100 - avoiding, source_scale: 'conflict_avoidance' }, confidence: 0.2, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'neuroticism', score: (avoiding + accommodating) / 2, source_scale: 'conflict_aversion' }, confidence: 0.2, source_type: source });
      break;
    }
    case 'decision-making-style': {
      const analytical = scaleMap.analytical ?? 50;
      const intuitive = scaleMap.intuitive ?? 50;
      const spontaneous = scaleMap.spontaneous ?? 50;
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'conscientiousness', score: analytical, source_scale: 'analytical_decision' }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'openness', score: intuitive, source_scale: 'intuitive_decision' }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'T_F', thinking_score: analytical, feeling_score: intuitive }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'J_P', judging_score: analytical, perceiving_score: spontaneous }, confidence: 0.25, source_type: source });
      break;
    }
    case 'leadership-style': {
      const directive = scaleMap.directive ?? 50;
      const coaching = scaleMap.coaching ?? 50;
      const delegating = scaleMap.delegating ?? 50;
      const visionary = scaleMap.visionary ?? 50;
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: (directive + visionary) / 2, source_scale: 'leadership_presence' }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: (coaching + delegating) / 2, source_scale: 'leadership_empowerment' }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'openness', score: visionary, source_scale: 'visionary_leadership' }, confidence: 0.2, source_type: source });
      break;
    }
    case 'career-explorer': {
      // Holland/RIASEC — direct framework signal
      const codeMap: Record<string, string> = { realistic: 'R', investigative: 'I', artistic: 'A', social: 'S', enterprising: 'E', conventional: 'C' };
      const riasecScores: Record<string, number> = {};
      for (const key of Object.keys(codeMap)) {
        riasecScores[codeMap[key]] = scaleMap[key] ?? 0;
      }
      const sorted = Object.entries(riasecScores).sort((a, b) => b[1] - a[1]);
      const hollandCode = sorted.slice(0, 3).map(e => e[0]).join('');
      signals.push({ framework: 'holland', signal_type: 'riasec_code', signal_data: { code: hollandCode, scores: riasecScores }, confidence: 0.55, source_type: source });
      // Holland also informs Big Five
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'openness', score: ((scaleMap.artistic ?? 0) + (scaleMap.investigative ?? 0)) / 2, source_scale: 'holland_AI' }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: ((scaleMap.enterprising ?? 0) + (scaleMap.social ?? 0)) / 2, source_scale: 'holland_ES' }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'conscientiousness', score: ((scaleMap.conventional ?? 0) + (scaleMap.realistic ?? 0)) / 2, source_scale: 'holland_CR' }, confidence: 0.2, source_type: source });
      break;
    }
    case 'core-motivation': {
      // Enneagram — direct framework signal
      const typeMap: Record<string, number> = { perfectionist: 1, helper: 2, achiever: 3, individualist: 4, investigator: 5, loyalist: 6, enthusiast: 7, challenger: 8, peacemaker: 9 };
      const sorted = Object.entries(scaleMap).sort((a, b) => b[1] - a[1]);
      const topType = sorted[0]?.[0];
      const topNum = typeMap[topType] ?? 0;
      const wingType = sorted[1]?.[0];
      const wingNum = typeMap[wingType] ?? 0;
      signals.push({ framework: 'enneagram', signal_type: 'type_hint', signal_data: { type: topNum, wing: wingNum, scores: scaleMap, label: `${topNum}w${wingNum}` }, confidence: 0.45, source_type: source });
      // Enneagram informs Big Five
      const agreeScore = ((scaleMap.helper ?? 0) + (scaleMap.peacemaker ?? 0)) / 2;
      const neuroScore = ((scaleMap.loyalist ?? 0) + (scaleMap.individualist ?? 0)) / 2;
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: agreeScore, source_scale: 'enneagram_29' }, confidence: 0.2, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'neuroticism', score: neuroScore, source_scale: 'enneagram_46' }, confidence: 0.2, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: ((scaleMap.enthusiast ?? 0) + (scaleMap.achiever ?? 0)) / 2, source_scale: 'enneagram_37' }, confidence: 0.2, source_type: source });
      break;
    }
    case 'workplace-style': {
      // DiSC — direct framework signal
      const d = scaleMap.dominance ?? 0, i = scaleMap.influence ?? 0, s = scaleMap.steadiness ?? 0, c = scaleMap.conscientiousness_d ?? 0;
      const discEntries = [{ key: 'D', score: d }, { key: 'i', score: i }, { key: 'S', score: s }, { key: 'C', score: c }].sort((a, b) => b.score - a.score);
      let discStyle = discEntries[0].key;
      if (discEntries[1].score >= 42 && discEntries[0].score - discEntries[1].score < 30) {
        discStyle = discEntries[0].key + discEntries[1].key;
      }
      signals.push({ framework: 'disc', signal_type: 'style_hint', signal_data: { style: discStyle, scores: { D: d, i, S: s, C: c } }, confidence: 0.5, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: (d + i) / 2, source_scale: 'disc_Di' }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: (s + i) / 2, source_scale: 'disc_iS' }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'conscientiousness', score: c, source_scale: 'disc_C' }, confidence: 0.3, source_type: source });
      break;
    }
    case 'perception-style': {
      const sensing = scaleMap.sensing ?? 50;
      const intuitionScore = scaleMap.intuition ?? 50;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'S_N', sensing_score: sensing, intuition_score: intuitionScore }, confidence: 0.5, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'openness', score: intuitionScore, source_scale: 'perception_intuition' }, confidence: 0.35, source_type: source });
      break;
    }
    case 'personal-values': {
      signals.push({ framework: 'values', signal_type: 'value_profile', signal_data: { scores: { ...scaleMap }, primary_label: result.primaryLabel }, confidence: 0.5, source_type: source });
      // Values inform alignment
      const moral = ((scaleMap.benevolence ?? 0) + (scaleMap.universalism ?? 0)) / 2;
      const order = ((scaleMap.tradition ?? 0) + (scaleMap.security ?? 0)) / 2;
      signals.push({ framework: 'alignment', signal_type: 'axis_hint', signal_data: { axis: 'moral', score: moral }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'alignment', signal_type: 'axis_hint', signal_data: { axis: 'order', score: order }, confidence: 0.25, source_type: source });
      break;
    }
    case 'emotional-stability': {
      const volatility = scaleMap.volatility ?? 50;
      const withdrawal = scaleMap.withdrawal ?? 50;
      const stressSens = scaleMap.stress_sensitivity ?? 50;
      const recovery = scaleMap.emotional_recovery ?? 50;
      const neuro = (volatility + withdrawal + stressSens + (100 - recovery)) / 4;
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'neuroticism', score: neuro, raw: scaleMap }, confidence: 0.45, source_type: source });
      signals.push({ framework: 'attachment', signal_type: 'style_hint', signal_data: { anxious_tendency: volatility, avoidant_tendency: withdrawal, stability: recovery }, confidence: 0.2, source_type: source });
      break;
    }
    case 'cognitive-functions': {
      // Map cognitive function scores to MBTI axes
      const Fi = scaleMap.Fi ?? 0, Fe = scaleMap.Fe ?? 0, Ti = scaleMap.Ti ?? 0, Te = scaleMap.Te ?? 0;
      const Ni = scaleMap.Ni ?? 0, Ne = scaleMap.Ne ?? 0, Si = scaleMap.Si ?? 0, Se = scaleMap.Se ?? 0;
      // E/I: extraverted functions vs introverted
      const extraverted = (Fe + Te + Ne + Se) / 4;
      const introverted = (Fi + Ti + Ni + Si) / 4;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'E_I', extroversion_score: extraverted, introversion_score: introverted }, confidence: 0.5, source_type: source });
      // S/N: sensing vs intuition
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'S_N', sensing_score: (Si + Se) / 2, intuition_score: (Ni + Ne) / 2 }, confidence: 0.5, source_type: source });
      // T/F: thinking vs feeling
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'T_F', thinking_score: (Ti + Te) / 2, feeling_score: (Fi + Fe) / 2 }, confidence: 0.5, source_type: source });
      // J/P: Je+Ji functions vs Pe+Pi
      const judging = (Te + Fe + Ni + Si) / 4;
      const perceiving = (Ti + Fi + Ne + Se) / 4;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'J_P', judging_score: judging, perceiving_score: perceiving }, confidence: 0.45, source_type: source });
      // Derive suggested MBTI from function stack
      const fnToType: Record<string, string> = { 'Fi-Ne': 'INFP', 'Fi-Se': 'ISFP', 'Fe-Ni': 'ENFJ', 'Fe-Si': 'ESFJ', 'Ti-Ne': 'INTP', 'Ti-Se': 'ISTP', 'Te-Ni': 'ENTJ', 'Te-Si': 'ESTJ', 'Ni-Fe': 'INFJ', 'Ni-Te': 'INTJ', 'Ne-Fi': 'ENFP', 'Ne-Ti': 'ENTP', 'Si-Fe': 'ISFJ', 'Si-Te': 'ISTJ', 'Se-Fi': 'ESFP', 'Se-Ti': 'ESTP' };
      const funcs = [{ fn: 'Fi', s: Fi }, { fn: 'Fe', s: Fe }, { fn: 'Ti', s: Ti }, { fn: 'Te', s: Te }, { fn: 'Ni', s: Ni }, { fn: 'Ne', s: Ne }, { fn: 'Si', s: Si }, { fn: 'Se', s: Se }].sort((a, b) => b.s - a.s);
      const stack = `${funcs[0].fn}-${funcs[1].fn}`;
      const suggestedMBTI = fnToType[stack];
      if (suggestedMBTI) {
        signals.push({ framework: 'mbti', signal_type: 'suggested_type', signal_data: { suggested: suggestedMBTI, stack, confidence_reason: 'cognitive_function_stack' }, confidence: 0.55, source_type: source });
      }
      break;
    }
    case 'thinking-feeling': {
      const logical = scaleMap.logical ?? 50, empathic = scaleMap.empathic ?? 50;
      const principled = scaleMap.principled ?? 50, contextual = scaleMap.contextual ?? 50;
      const thinkingScore = (logical + principled) / 2;
      const feelingScore = (empathic + contextual) / 2;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'T_F', thinking_score: thinkingScore, feeling_score: feelingScore }, confidence: 0.5, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: empathic, source_scale: 'tf_empathic' }, confidence: 0.35, source_type: source });
      break;
    }
    case 'structure-spontaneity': {
      const planning = scaleMap.planning ?? 50, flexibility = scaleMap.flexibility ?? 50;
      const closure = scaleMap.closure ?? 50, exploration = scaleMap.exploration ?? 50;
      const judgingScore = (planning + closure) / 2;
      const perceivingScore = (flexibility + exploration) / 2;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'J_P', judging_score: judgingScore, perceiving_score: perceivingScore }, confidence: 0.5, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'conscientiousness', score: judgingScore, source_scale: 'structure_planning' }, confidence: 0.45, source_type: source });
      break;
    }
    case 'social-energy': {
      const battery = scaleMap.social_battery ?? 50, initiative = scaleMap.social_initiative ?? 50;
      const recharge = scaleMap.recharge_mode ?? 50;
      const extScore = (battery + initiative) / 2;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'E_I', extroversion_score: extScore, introversion_score: recharge }, confidence: 0.5, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: extScore, source_scale: 'social_energy' }, confidence: 0.5, source_type: source });
      signals.push({ framework: 'attachment', signal_type: 'style_hint', signal_data: { social_engagement: extScore, recharge_need: recharge }, confidence: 0.2, source_type: source });
      break;
    }
    case 'keirsey-temperament': {
      const guardian = scaleMap.guardian ?? 0, artisan = scaleMap.artisan ?? 0;
      const idealist = scaleMap.idealist ?? 0, rational = scaleMap.rational ?? 0;
      const sorted = [{ k: 'guardian', s: guardian }, { k: 'artisan', s: artisan }, { k: 'idealist', s: idealist }, { k: 'rational', s: rational }].sort((a, b) => b.s - a.s);
      const primary = sorted[0].k;
      // Keirsey to MBTI axis mapping: SJ=guardian, SP=artisan, NF=idealist, NT=rational
      const sScore = (guardian + artisan) / 2; // S-types
      const nScore = (idealist + rational) / 2; // N-types
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'S_N', sensing_score: sScore, intuition_score: nScore }, confidence: 0.45, source_type: source });
      // T/F: only Rational=T, Idealist=F; Guardian/Artisan split evenly and shouldn't inform T/F
      const tScore = rational;
      const fScore = idealist;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'T_F', thinking_score: tScore, feeling_score: fScore }, confidence: 0.4, source_type: source });
      // J/P from temperament
      const jScore = (guardian + rational) / 2;
      const pScore = (artisan + idealist) / 2;
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'J_P', judging_score: jScore, perceiving_score: pScore }, confidence: 0.3, source_type: source });
      break;
    }
    case 'instinctual-variants': {
      const sp = scaleMap.sp ?? 0, so = scaleMap.so ?? 0, sx = scaleMap.sx ?? 0;
      const instincts = [{ k: 'sp', s: sp }, { k: 'so', s: so }, { k: 'sx', s: sx }].sort((a, b) => b.s - a.s);
      const stacking = instincts.map(i => i.k).join('/');
      signals.push({ framework: 'enneagram', signal_type: 'instinctual_variant', signal_data: { stacking, scores: { sp, so, sx }, dominant: instincts[0].k, blind_spot: instincts[2].k }, confidence: 0.45, source_type: source });
      // Instinctual variants inform Big Five
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: (so + sx) / 2, source_scale: 'instinct_social_sexual' }, confidence: 0.2, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'neuroticism', score: sp, source_scale: 'instinct_sp_anxiety' }, confidence: 0.15, source_type: source });
      break;
    }
    case 'socionics-quicktype': {
      const logic = scaleMap.logic ?? 50, ethics = scaleMap.ethics ?? 50;
      const sensingS = scaleMap.sensing_s ?? 50, intuitionS = scaleMap.intuition_s ?? 50;
      const extratim = scaleMap.extratim ?? 50, introtim = scaleMap.introtim ?? 50;
      const processS = scaleMap.process ?? 50, resultS = scaleMap.result ?? 50;
      const isE = extratim >= introtim, isL = logic >= ethics, isS = sensingS >= intuitionS, isP = processS >= resultS;
      const typeMap: Record<string, string> = {
        'E-L-S-P': 'LSE', 'E-L-S-R': 'SLE', 'E-L-N-P': 'LIE', 'E-L-N-R': 'ILE',
        'E-E-S-P': 'ESE', 'E-E-S-R': 'SEE', 'E-E-N-P': 'EIE', 'E-E-N-R': 'IEE',
        'I-L-S-P': 'LSI', 'I-L-S-R': 'SLI', 'I-L-N-P': 'LII', 'I-L-N-R': 'ILI',
        'I-E-S-P': 'ESI', 'I-E-S-R': 'SEI', 'I-E-N-P': 'EII', 'I-E-N-R': 'IEI',
      };
      const key = `${isE ? 'E' : 'I'}-${isL ? 'L' : 'E'}-${isS ? 'S' : 'N'}-${isP ? 'P' : 'R'}`;
      const socionicsType = typeMap[key] || 'Unknown';
      signals.push({ framework: 'socionics', signal_type: 'type_hint', signal_data: { type: socionicsType, scores: { logic, ethics, sensing: sensingS, intuition: intuitionS, extraversion: extratim, introversion: introtim, process: processS, result: resultS } }, confidence: 0.55, source_type: source });
      // Cross-validate with MBTI axes
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'E_I', extroversion_score: extratim, introversion_score: introtim }, confidence: 0.35, source_type: source });
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'T_F', thinking_score: logic, feeling_score: ethics }, confidence: 0.35, source_type: source });
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'S_N', sensing_score: sensingS, intuition_score: intuitionS }, confidence: 0.35, source_type: source });
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'J_P', judging_score: processS, perceiving_score: resultS }, confidence: 0.25, source_type: source });
      break;
    }
    case 'sloan-lifestyle': {
      const social = scaleMap.social ?? 50, limbic = scaleMap.limbic ?? 50;
      const organized = scaleMap.organized ?? 50, accommodating = scaleMap.accommodating ?? 50;
      const inquisitive = scaleMap.inquisitive ?? 50;
      const s = social >= 50 ? 'S' : 'R', l = limbic >= 50 ? 'L' : 'C', o = organized >= 50 ? 'O' : 'U';
      const a = accommodating >= 50 ? 'A' : 'E', i = inquisitive >= 50 ? 'I' : 'N';
      const sloanCode = `${s}${l}${o}${a}${i}`;
      signals.push({ framework: 'sloan', signal_type: 'type_hint', signal_data: { code: sloanCode, scores: { social, limbic, organized, accommodating, inquisitive } }, confidence: 0.5, source_type: source });
      // SLOAN maps directly to Big Five
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: social, source_scale: 'sloan_social' }, confidence: 0.45, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'neuroticism', score: limbic, source_scale: 'sloan_limbic' }, confidence: 0.45, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'conscientiousness', score: organized, source_scale: 'sloan_organized' }, confidence: 0.45, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: accommodating, source_scale: 'sloan_accommodating' }, confidence: 0.45, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'openness', score: inquisitive, source_scale: 'sloan_inquisitive' }, confidence: 0.45, source_type: source });
      // SLOAN informs MBTI
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'E_I', extroversion_score: social }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'S_N', intuition_score: inquisitive, sensing_score: 100 - inquisitive }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'J_P', judging_score: organized, perceiving_score: 100 - organized }, confidence: 0.3, source_type: source });
      break;
    }
    case 'communication-style': {
      const direct = scaleMap.direct ?? 50;
      const analytical = scaleMap.analytical ?? 50;
      const expressive = scaleMap.expressive ?? 50;
      const diplomatic = scaleMap.diplomatic ?? 50;
      // Communication style informs Big Five
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: (direct + expressive) / 2, source_scale: 'comm_direct_expressive' }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: (diplomatic + expressive) / 2, source_scale: 'comm_diplomatic' }, confidence: 0.3, source_type: source });
      // T/F from communication style
      signals.push({ framework: 'mbti', signal_type: 'dimension_hint', signal_data: { dimension: 'T_F', thinking_score: (analytical + direct) / 2, feeling_score: (expressive + diplomatic) / 2 }, confidence: 0.3, source_type: source });
      break;
    }
    case 'moral-compass': {
      const utilitarian = scaleMap.utilitarian ?? 50;
      const deontological = scaleMap.deontological ?? 50;
      const virtue = scaleMap.virtue ?? 50;
      const care = scaleMap.care ?? 50;
      // Moral compass informs D&D alignment
      const moralScore = (care + virtue) / 2; // Higher = more good-aligned
      const orderScore = (deontological + utilitarian) / 2; // Higher = more lawful
      signals.push({ framework: 'alignment', signal_type: 'moral_axis_hint', signal_data: { axis: 'moral', score: moralScore, raw: { utilitarian, deontological, virtue, care } }, confidence: 0.4, source_type: source });
      signals.push({ framework: 'alignment', signal_type: 'axis_hint', signal_data: { axis: 'order', score: orderScore }, confidence: 0.3, source_type: source });
      // Big Five correlates
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: (care + virtue) / 2, source_scale: 'moral_care_virtue' }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'openness', score: (virtue + utilitarian) / 2, source_scale: 'moral_openness' }, confidence: 0.2, source_type: source });
      break;
    }
    case 'attachment-style': {
      const secure = scaleMap.secure ?? 50;
      const anxious = scaleMap.anxious ?? 50;
      const avoidant = scaleMap.avoidant ?? 50;
      const fearful = scaleMap.fearful ?? 50;
      // Direct attachment framework signal — highest confidence quiz source
      signals.push({ framework: 'attachment', signal_type: 'style_scores', signal_data: { secure, anxious, avoidant, fearful, primary_label: result.primaryLabel }, confidence: 0.55, source_type: source });
      // Cross-signals to Big Five
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'neuroticism', score: (anxious + fearful) / 2, source_scale: 'attachment_anxiety' }, confidence: 0.3, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'extraversion', score: 100 - ((avoidant + fearful) / 2), source_scale: 'attachment_avoidance' }, confidence: 0.25, source_type: source });
      signals.push({ framework: 'big_five', signal_type: 'dimension_hint', signal_data: { dimension: 'agreeableness', score: (secure + 100 - avoidant) / 2, source_scale: 'attachment_trust' }, confidence: 0.25, source_type: source });
      break;
    }
    default:
      signals.push({ framework: 'unknown', signal_type: 'raw_quiz_result', signal_data: { slug: result.testSlug, scales: scaleMap, label: result.primaryLabel }, confidence: 0.1, source_type: source });
  }

  return signals;
}

// ── Universal mapper for shared-pool (themed) quizzes ──

function mapThemedQuizToSignals(result: GuestTestResult, poolAnswers: StoredPoolAnswer[]): PersonalitySignal[] {
  const signals: PersonalitySignal[] = [];
  const source = `guest-quiz:${result.testSlug}`;

  // Get relevant answers for this quiz's questions
  const quizQuestionIds = new Set(result.scales.map(s => s.key));
  const relevantAnswers = poolAnswers.filter(a => {
    const q = getSharedQuestionById(a.questionId);
    return q && quizQuestionIds.has(q.dimension);
  });

  if (relevantAnswers.length === 0) return signals;

  const aggregated = aggregatePoolAnswersToFrameworks(relevantAnswers);

  // Emit Big Five signals
  for (const [dim, data] of Object.entries(aggregated.big_five)) {
    if (data.count === 0) continue;
    const dimNames: Record<string, string> = { O: 'openness', C: 'conscientiousness', E: 'extraversion', A: 'agreeableness', N: 'neuroticism' };
    signals.push({
      framework: 'big_five', signal_type: 'dimension_hint',
      signal_data: { dimension: dimNames[dim] || dim, score: data.score, question_count: data.count },
      confidence: Math.min(0.15 + data.count * 0.05, 0.6),
      source_type: source,
    });
  }

  // Emit MBTI signals
  for (const [dim, data] of Object.entries(aggregated.mbti)) {
    if (data.count === 0) continue;
    signals.push({
      framework: 'mbti', signal_type: 'dimension_hint',
      signal_data: { dimension: dim, score: data.score, question_count: data.count },
      confidence: Math.min(0.1 + data.count * 0.05, 0.5),
      source_type: source,
    });
  }

  // Emit Attachment signals
  for (const [style, data] of Object.entries(aggregated.attachment)) {
    if (data.count === 0) continue;
    signals.push({
      framework: 'attachment', signal_type: 'style_hint',
      signal_data: { style, score: data.score, question_count: data.count },
      confidence: Math.min(0.1 + data.count * 0.04, 0.4),
      source_type: source,
    });
  }

  // Emit Alignment signals
  for (const [axis, data] of Object.entries(aggregated.alignment)) {
    if (data.count === 0) continue;
    signals.push({
      framework: 'alignment', signal_type: 'axis_hint',
      signal_data: { axis, score: data.score, question_count: data.count },
      confidence: Math.min(0.1 + data.count * 0.05, 0.5),
      source_type: source,
    });
  }

  return signals;
}

// ── Public API ──

/**
 * Maps any guest quiz result into personality framework signals.
 * Automatically uses the universal mapper for themed quizzes
 * or the legacy per-quiz mapper for original quizzes.
 */
export function mapGuestResultToSignals(result: GuestTestResult, isThemed = false): PersonalitySignal[] {
  if (isThemed) {
    const poolAnswers = getStoredPoolAnswers();
    return mapThemedQuizToSignals(result, poolAnswers);
  }
  return mapLegacyQuizToSignals(result);
}

/**
 * Aggregate ALL stored pool answers across all themed quizzes into signals.
 * Used during account migration for maximum confidence.
 */
export function mapAllPoolAnswersToSignals(): PersonalitySignal[] {
  const poolAnswers = getStoredPoolAnswers();
  if (poolAnswers.length === 0) return [];

  const signals: PersonalitySignal[] = [];
  const source = 'guest-quiz:shared-pool-aggregate';
  const aggregated = aggregatePoolAnswersToFrameworks(poolAnswers);

  const dimNames: Record<string, string> = { O: 'openness', C: 'conscientiousness', E: 'extraversion', A: 'agreeableness', N: 'neuroticism' };

  for (const [dim, data] of Object.entries(aggregated.big_five)) {
    if (data.count === 0) continue;
    signals.push({
      framework: 'big_five', signal_type: 'dimension_hint',
      signal_data: { dimension: dimNames[dim] || dim, score: data.score, question_count: data.count, aggregated: true },
      confidence: Math.min(0.2 + data.count * 0.05, 0.7),
      source_type: source,
    });
  }

  for (const [dim, data] of Object.entries(aggregated.mbti)) {
    if (data.count === 0) continue;
    signals.push({
      framework: 'mbti', signal_type: 'dimension_hint',
      signal_data: { dimension: dim, score: data.score, question_count: data.count, aggregated: true },
      confidence: Math.min(0.15 + data.count * 0.05, 0.6),
      source_type: source,
    });
  }

  for (const [style, data] of Object.entries(aggregated.attachment)) {
    if (data.count === 0) continue;
    signals.push({
      framework: 'attachment', signal_type: 'style_hint',
      signal_data: { style, score: data.score, question_count: data.count, aggregated: true },
      confidence: Math.min(0.1 + data.count * 0.04, 0.5),
      source_type: source,
    });
  }

  for (const [axis, data] of Object.entries(aggregated.alignment)) {
    if (data.count === 0) continue;
    signals.push({
      framework: 'alignment', signal_type: 'axis_hint',
      signal_data: { axis, score: data.score, question_count: data.count, aggregated: true },
      confidence: Math.min(0.1 + data.count * 0.05, 0.6),
      source_type: source,
    });
  }

  return signals;
}

// ── localStorage helpers ──

const IMPLICIT_MEDIA_KEY = 'psyforge:anon:implicit_media';

export interface ImplicitMediaEntry {
  category: string;
  title: string;
  confidence: number;
  source: string;
  addedAt: string;
}

export function saveImplicitMediaInterests(
  interests: Array<{ category: string; title: string; confidence: number }>,
  quizSlug: string
): void {
  try {
    const existing = getStoredImplicitMedia();
    const newEntries: ImplicitMediaEntry[] = interests.map(i => ({
      ...i,
      source: `quiz:${quizSlug}`,
      addedAt: new Date().toISOString(),
    }));
    // Deduplicate by title+category
    const merged = [...existing];
    for (const entry of newEntries) {
      if (!merged.some(e => e.title === entry.title && e.category === entry.category)) {
        merged.push(entry);
      }
    }
    localStorage.setItem(IMPLICIT_MEDIA_KEY, JSON.stringify(merged));
  } catch { /* silent */ }
}

export function getStoredImplicitMedia(): ImplicitMediaEntry[] {
  try {
    const raw = localStorage.getItem(IMPLICIT_MEDIA_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function clearStoredImplicitMedia(): void {
  try { localStorage.removeItem(IMPLICIT_MEDIA_KEY); } catch { /* silent */ }
}

export function saveGuestQuizResult(result: GuestTestResult): void {
  try {
    const existing = getStoredGuestResults();
    const filtered = existing.filter(r => r.testSlug !== result.testSlug);
    filtered.push(result);
    localStorage.setItem(GUEST_RESULTS_KEY, JSON.stringify(filtered));
  } catch { /* silent */ }
}

export function getStoredGuestResults(): GuestTestResult[] {
  try {
    const raw = localStorage.getItem(GUEST_RESULTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function clearStoredGuestResults(): void {
  try { localStorage.removeItem(GUEST_RESULTS_KEY); } catch { /* silent */ }
}
